import { useEffect, useState } from 'react';
import { Heart, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRESETS = [1, 2, 5, 10];

export default function ArticleDonation({ article }) {
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState({ count: 0, total_eur: 0 });
  const [amount, setAmount] = useState(2);
  const [custom, setCustom] = useState('');
  const [provider, setProvider] = useState('stripe');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!article?.slug) return;
    fetch(`${API}/public/articles/${article.slug}/donations-stats`).then(r => r.json()).then(setStats).catch(() => {});
  }, [article?.slug]);

  async function donate() {
    const amt = custom ? parseFloat(custom) : amount;
    if (!amt || amt < 0.5) { toast.error('Minimalno 0.50 €'); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API}/public/donations/create`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amt, currency: 'eur', provider, article_id: article?.id }),
      });
      let d;
      try { d = await r.json(); } catch { d = null; }
      if (!r.ok) {
        const msg = d?.detail || d?.message || `HTTP ${r.status}`;
        throw new Error(msg);
      }
      if (!d?.ok || !d?.approval_url) throw new Error('Nedostaje approval_url u odgovoru');
      window.location.href = d.approval_url;
    } catch (e) {
      console.error('Donation create failed:', e);
      toast.error(`Greška pri kreiranju donacije: ${e.message || e}`);
    } finally { setLoading(false); }
  }

  return (
    <>
      <div className="flex items-center flex-wrap gap-3 mb-5" data-testid="article-donation">
        <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-600 hover:bg-red-700 text-white" data-testid="donation-open">
          <Heart size={13} /> Podrži članak
        </button>
        {stats.count > 0 && (
          <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1" data-testid="donation-stats">
            💚 {stats.count} {stats.count === 1 ? 'korisnik' : 'korisnika'} podržalo ovaj članak
          </span>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="donation-modal">
          <div className="bg-white dark:bg-[#0f172a] rounded-xl w-full max-w-md p-6 border border-gray-200 dark:border-gray-800" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-bold flex items-center gap-2 text-gray-900 dark:text-gray-100"><Heart size={18} className="text-red-600" /> Podrži članak</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Tvoja donacija izravno pomaže autorima i redakciji.</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"><X size={20} /></button>
            </div>

            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Odaberi iznos (EUR)</p>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {PRESETS.map(p => (
                <button key={p} onClick={() => { setAmount(p); setCustom(''); }}
                  className={`py-2 rounded-lg text-sm font-semibold border transition-colors ${amount === p && !custom ? 'bg-red-600 text-white border-red-600' : 'bg-white dark:bg-[#0b1220] text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:border-red-400'}`}
                  data-testid={`donation-preset-${p}`}>
                  {p} €
                </button>
              ))}
            </div>
            <input type="number" step="0.5" min="0.5" placeholder="Vlastiti iznos (min 0.50)"
              value={custom} onChange={e => setCustom(e.target.value)}
              className="w-full bg-gray-50 dark:bg-[#0b1220] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 mb-4 outline-none focus:border-red-400"
              data-testid="donation-custom" />

            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Način plaćanja</p>
            <div className="grid grid-cols-2 gap-2 mb-5">
              <button onClick={() => setProvider('stripe')}
                className={`py-2 rounded-lg text-xs font-semibold border ${provider === 'stripe' ? 'bg-red-600 text-white border-red-600' : 'bg-white dark:bg-[#0b1220] text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700'}`}
                data-testid="donation-provider-stripe">Stripe</button>
              <button onClick={() => setProvider('paypal')}
                className={`py-2 rounded-lg text-xs font-semibold border ${provider === 'paypal' ? 'bg-red-600 text-white border-red-600' : 'bg-white dark:bg-[#0b1220] text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700'}`}
                data-testid="donation-provider-paypal">PayPal</button>
            </div>
            <button onClick={donate} disabled={loading}
              className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50"
              data-testid="donation-submit">
              {loading ? 'Pripremam…' : `Doniraj ${custom || amount} €`}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
